import { useState, useEffect, useCallback } from 'react';
import { Account } from '../types';
import http from '../services/http';
import { DEUTSCHE_BANK_ACCOUNTS_BASE } from '@/config/deutscheBankDemo';

// Module-scoped pending promise to deduplicate concurrent requests (helps in StrictMode)
let pendingAccountsRequest: Promise<Account[] | null> | null = null;

type AccountsEnvelope = {
  accounts?: Account[];
};

export const normalizeAccountsResponse = (data: unknown): Account[] => {
  if (Array.isArray(data)) {
    return data as Account[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as AccountsEnvelope).accounts)) {
    return (data as AccountsEnvelope).accounts!;
  }

  return [];
};

export const useAccounts = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      if (!pendingAccountsRequest) {
        pendingAccountsRequest = (async () => {
          const res = await http.get(DEUTSCHE_BANK_ACCOUNTS_BASE);
          return normalizeAccountsResponse(res.data);
        })();
      }

      const data = await pendingAccountsRequest;
      pendingAccountsRequest = null;

      if (data) setAccounts(data as Account[]);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch accounts'));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return { accounts, loading, error };
};
